/**
 * venueData.ts
 * Loads and indexes all Mappedin MVF data needed for navigation.
 * Runs once and caches — all lookups are O(1).
 */

import buildingsMetadata from '../../data/venue/buildings_metadata.json';

export interface NavNode {
  id: string;
  lng: number;
  lat: number;
  space: string[]; // space IDs this node sits inside
  neighbors: { id: string; weight: number }[];
  floorId?: string;
}

export interface LocationEntry {
  id: string;
  name: string;
  spaceId: string; // first space id
  floorId: string;
}

export interface VenueData {
  /** All nav nodes indexed by ID */
  nodeById: Map<string, NavNode>;
  /** Space ID → list of destinationNode IDs (entry points into that space) */
  spaceToDestNodes: Map<string, string[]>;
  /** Flat list of named locations for search */
  locations: LocationEntry[];
}

let cachedVenues: Record<string, VenueData> = {};
const inflightLoads: Record<string, Promise<VenueData>> = {};

// Per-floor data is loaded first for fast rendering; full graph loaded later for navigation
const partialVenues: Record<string, { nodeById: Map<string, NavNode>; spaceToDestNodes: Map<string, string[]>; locations: LocationEntry[] }> = {};

function buildVenueFromJson(bid: string, nodeGeoJSON: any, spaceGeoJSON: any, rawLocations: any[], allSpaceFloors?: Record<string, string>): VenueData {
  const nodeById = new Map<string, NavNode>();
  for (const f of nodeGeoJSON.features) {
    const props = f.properties;
    const [lng, lat] = f.geometry.coordinates;
    nodeById.set(props.id, {
      id: props.id,
      lng,
      lat,
      space: props.space ?? [],
      neighbors: props.neighbors ?? [],
      floorId: props.floor || props.map,
    });
  }

  const nodesByFloor = new Map<string, NavNode[]>();
  for (const node of nodeById.values()) {
    if (node.floorId) {
      if (!nodesByFloor.has(node.floorId)) nodesByFloor.set(node.floorId, []);
      nodesByFloor.get(node.floorId)!.push(node);
    }
  }

  const spaceToDestNodes = new Map<string, string[]>();
  for (const f of spaceGeoJSON.features) {
    const spaceId = f.properties.id;
    const destNodes: string[] = f.properties.destinationNodes ?? [];
    if (destNodes.length > 0) {
      spaceToDestNodes.set(spaceId, [...destNodes]);
    } else {
      let pLng = 0, pLat = 0, hasCoords = false;
      if (f.geometry.type === 'Point') {
        const coords = f.geometry.coordinates;
        if (coords?.length >= 2) { pLng = coords[0]; pLat = coords[1]; hasCoords = true; }
      } else if (f.geometry.type === 'Polygon') {
        const coords = f.geometry.coordinates[0];
        if (coords?.length > 0) {
          pLng = coords.reduce((sum: number, pt: any) => sum + pt[0], 0) / coords.length;
          pLat = coords.reduce((sum: number, pt: any) => sum + pt[1], 0) / coords.length;
          hasCoords = true;
        }
      } else if (f.geometry.type === 'MultiPolygon') {
        const coords = f.geometry.coordinates[0]?.[0];
        if (coords?.length > 0) {
          pLng = coords.reduce((sum: number, pt: any) => sum + pt[0], 0) / coords.length;
          pLat = coords.reduce((sum: number, pt: any) => sum + pt[1], 0) / coords.length;
          hasCoords = true;
        }
      }
      if (hasCoords) {
        const floorId = f.properties.floorId || f.properties.floor || f.properties.map;
        let closestNodeId: string | null = null;
        let minD = Infinity;
        const floorNodes = nodesByFloor.get(floorId) || [];
        for (const node of floorNodes) {
          const dx = node.lng - pLng;
          const dy = node.lat - pLat;
          const d = dx * dx + dy * dy;
          if (d < minD) { minD = d; closestNodeId = node.id; }
        }
        if (closestNodeId && minD < 0.001) spaceToDestNodes.set(spaceId, [closestNodeId]);
      }
    }
  }

  for (const node of nodeById.values()) {
    for (const spaceId of node.space) {
      if (!spaceToDestNodes.has(spaceId)) {
        spaceToDestNodes.set(spaceId, [node.id]);
      } else {
        const existing = spaceToDestNodes.get(spaceId)!;
        if (!existing.includes(node.id)) existing.push(node.id);
      }
    }
  }

  const locations: LocationEntry[] = [];
  const floorsInfo = buildingsMetadata[bid as keyof typeof buildingsMetadata]?.floors || [
    { id: 'm_fa5a3ee91d369d98', prefix: '1', name: 'Ground Floor' },
    { id: 'first', prefix: '2', name: 'First Floor' },
    { id: 'second', prefix: '3', name: 'Second Floor' },
    { id: 'third', prefix: '4', name: 'Third Floor' },
  ];
  const spaceToFloorId = new Map<string, string>();
  for (const f of spaceGeoJSON.features) {
    const spaceId = f.properties?.id;
    const floorId = f.properties?.floorId;
    if (spaceId && floorId) spaceToFloorId.set(spaceId, floorId);
  }
  const getSpaceFloorId = (spaceId: string): string | undefined => {
    if (spaceToFloorId.has(spaceId)) return spaceToFloorId.get(spaceId);
    // Partial loads only contain one floor's spaces — fall back to the full index
    if (allSpaceFloors?.[spaceId]) return allSpaceFloors[spaceId];
    const dests = spaceToDestNodes.get(spaceId);
    if (dests?.length) return nodeById.get(dests[0])?.floorId;
    return undefined;
  };

  for (const loc of rawLocations) {
    const locName = loc.name?.trim();
    if (!locName || !loc.spaces?.length) continue;
    if (bid === 'ab4' || bid === 'ab1' || bid === 'ab5') {
      const spacesByFloor = new Map<string, string>();
      for (const sp of loc.spaces) {
        const sfid = getSpaceFloorId(sp.id);
        if (sfid && !spacesByFloor.has(sfid)) spacesByFloor.set(sfid, sp.id);
      }
      spacesByFloor.forEach((spaceId, floorId) => {
        const fl = floorsInfo.find(f => f.id === floorId);
        if (fl) {
          let displayName = locName;
          if (displayName.toLowerCase() === 'lift') displayName = 'Elevator & Stairs';
          const isNum = /^\d+$/.test(displayName);
          if (isNum) displayName = fl.prefix + displayName.substring(1);
          else displayName = `${displayName} (${fl.name})`;
          locations.push({ id: `${loc.id}_${fl.id}`, name: displayName, spaceId, floorId: fl.id });
        }
      });
    } else {
      const firstValidSpaceId = loc.spaces.find((sp: any) => spaceToDestNodes.has(sp.id))?.id;
      if (firstValidSpaceId) {
        floorsInfo.forEach(fl => {
          let displayName = locName;
          if (displayName.toLowerCase() === 'lift') displayName = 'Elevator & Stairs';
          const isNum = /^\d+$/.test(displayName);
          if (isNum) displayName = fl.prefix + displayName.substring(1);
          else displayName = `${displayName} (${fl.name})`;
          locations.push({ id: `${loc.id}_${fl.id}`, name: displayName, spaceId: firstValidSpaceId, floorId: fl.id });
        });
      }
    }
  }
  locations.sort((a, b) => a.name.localeCompare(b.name));

  return { nodeById, spaceToDestNodes, locations };
}

function healGraph(nodeById: Map<string, NavNode>) {
  const nodesByFloor = new Map<string, string[]>();
  for (const [nodeId, node] of nodeById.entries()) {
    if (!node.floorId) continue;
    let list = nodesByFloor.get(node.floorId);
    if (!list) { list = []; nodesByFloor.set(node.floorId, list); }
    list.push(nodeId);
  }

  const getDistMeters = (n1: NavNode, n2: NavNode) => {
    const dx = (n1.lng - n2.lng) * 111320 * Math.cos(n1.lat * Math.PI / 180);
    const dy = (n1.lat - n2.lat) * 110540;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const gridSize = 0.0002;
  const getGridKey = (lat: number, lng: number) => `${Math.floor(lat / gridSize)}_${Math.floor(lng / gridSize)}`;

  for (const [, floorNodeIds] of nodesByFloor.entries()) {
    const visited = new Set<string>();
    const components: { nodeId: string; size: number; nodes: string[] }[] = [];

    for (const nodeId of floorNodeIds) {
      if (visited.has(nodeId)) continue;
      const comp = new Set<string>();
      const queue = [nodeId];
      comp.add(nodeId);
      visited.add(nodeId);
      let qHead = 0;
      while (qHead < queue.length) {
        const curr = queue[qHead++];
        const node = nodeById.get(curr);
        if (!node) continue;
        for (const nb of node.neighbors) {
          if (!nodeById.has(nb.id)) continue;
          const neighborNode = nodeById.get(nb.id)!;
          if (neighborNode.floorId === node.floorId && !visited.has(nb.id)) {
            visited.add(nb.id);
            comp.add(nb.id);
            queue.push(nb.id);
          }
        }
      }
      components.push({ nodeId, size: comp.size, nodes: Array.from(comp) });
    }

    if (components.length <= 1) continue;
    components.sort((a, b) => b.size - a.size);
    const mainCompNodes = new Set<string>(components[0].nodes);
    const grid = new Map<string, NavNode[]>();
    for (const nodeId of mainCompNodes) {
      const mn = nodeById.get(nodeId);
      if (!mn) continue;
      const key = getGridKey(mn.lat, mn.lng);
      let cell = grid.get(key);
      if (!cell) { cell = []; grid.set(key, cell); }
      cell.push(mn);
    }

    for (let i = 1; i < components.length; i++) {
      const comp = components[i];
      let bestMainNode: NavNode | null = null;
      let bestCompNode: NavNode | null = null;
      let minD = Infinity;
      for (const compNodeId of comp.nodes) {
        const cn = nodeById.get(compNodeId);
        if (!cn) continue;
        const latBin = Math.floor(cn.lat / gridSize);
        const lngBin = Math.floor(cn.lng / gridSize);
        for (let dl = -1; dl <= 1; dl++) {
          for (let dg = -1; dg <= 1; dg++) {
            const cellNodes = grid.get(`${latBin + dl}_${lngBin + dg}`);
            if (!cellNodes) continue;
            for (const mn of cellNodes) {
              const d = getDistMeters(cn, mn);
              if (d < minD) { minD = d; bestMainNode = mn; bestCompNode = cn; }
            }
          }
        }
      }
      if (minD < 15 && bestMainNode && bestCompNode) {
        bestMainNode.neighbors.push({ id: bestCompNode.id, weight: minD * 30 });
        bestCompNode.neighbors.push({ id: bestMainNode.id, weight: minD * 30 });
        for (const nid of comp.nodes) {
          mainCompNodes.add(nid);
          const node = nodeById.get(nid);
          if (node) {
            const key = getGridKey(node.lat, node.lng);
            let cell = grid.get(key);
            if (!cell) { cell = []; grid.set(key, cell); }
            cell.push(node);
          }
        }
      }
    }
  }
}

export async function loadVenueData(buildingId: string = 'ab3'): Promise<VenueData> {
  const bid = buildingId.toLowerCase();
  if (cachedVenues[bid]) return cachedVenues[bid];
  const existing = inflightLoads[bid];
  if (existing) return existing;

  // Always resolves with the FULL all-floor graph (partial venues are never returned here)
  const loadPromise = (async () => {
    const res = await fetch(`/venue/${bid}_inside.json`);
    if (!res.ok) throw new Error(`Failed to load building data for ${buildingId}`);
    const { nodeGeoJSON, spaceGeoJSON, rawLocations } = await res.json();

    const venue = buildVenueFromJson(bid, nodeGeoJSON, spaceGeoJSON, rawLocations);

    // Heal graph off main thread
    await new Promise<void>((resolve) => {
      const runHeal = () => { healGraph(venue.nodeById); resolve(); };
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(runHeal, { timeout: 2000 });
      } else {
        setTimeout(runHeal, 0);
      }
    });

    cachedVenues[bid] = venue;
    delete partialVenues[bid];
    return venue;
  })();

  inflightLoads[bid] = loadPromise;
  try {
    return await loadPromise;
  } finally {
    delete inflightLoads[bid];
  }
}

/**
 * Fast partial load — fetches only the current floor's node subset.
 * Returns a VenueData that has locations but limited routing (only floor nodes).
 * Used for initial render; full graph loads in background via loadVenueData.
 */
export async function loadVenuePartial(buildingId: string, floorId: string): Promise<VenueData> {
  const bid = buildingId.toLowerCase();
  if (cachedVenues[bid]) return cachedVenues[bid];
  if (partialVenues[bid]) return partialVenues[bid];

  try {
    const res = await fetch(`/venue/${bid}_${floorId}.json`);
    if (!res.ok) throw new Error('Partial load failed');
    const { nodeGeoJSON, spaceGeoJSON, rawLocations, spaceFloors } = await res.json();
    const venue = buildVenueFromJson(bid, nodeGeoJSON, spaceGeoJSON, rawLocations, spaceFloors);
    partialVenues[bid] = venue;
    return venue;
  } catch {
    // Fallback: full load
    return loadVenueData(buildingId);
  }
}

/**
 * Resolves immediately if the full (all-floor) venue graph is already loaded.
 * Otherwise loads it — call before route calculation that may cross floors.
 */
export async function ensureFullVenue(buildingId: string): Promise<VenueData> {
  return loadVenueData(buildingId);
}
